import { Box, Button, FormControl, IconButton, MenuItem, Select, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { BrandLogo } from '../../components';
import ThemeToggle from '../ThemeToggle';
import { UserMenu } from './UserMenu';
import { ROUTES } from '../../utils';
import { LeftPanelCloseIcon, LeftPanelOpenIcon } from '../../../assets/icons';
import { useClientScope } from '../../contexts';
import yodhaLogo from '../../../assets/yodha-logo.png';
import { adminNavigationSections, findActiveNavItem } from './adminNavigation';

interface AdminAppHeaderProps {
  onMenu: () => void;
  drawerOpen: boolean;
}

export const AdminAppHeader = ({ onMenu, drawerOpen }: AdminAppHeaderProps) => {
  const location = useLocation();
  const { isCentralAdmin, clients, selectedClientId, setSelectedClientId } = useClientScope();

  const navItems = adminNavigationSections.flatMap((section) =>
    section.items.filter((item) => isCentralAdmin || !item.centralOnly),
  );
  const currentPage = findActiveNavItem(location.pathname, navItems);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 3,
        py: 1,
        position: 'relative',
        zIndex: 10,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <IconButton edge='start' onClick={onMenu} sx={{ mr: 2 }}>
        {drawerOpen ? <LeftPanelCloseIcon /> : <LeftPanelOpenIcon />}
      </IconButton>
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant='subtitle2' noWrap>
          {currentPage?.text}
        </Typography>
      </Box>
      <Box display='flex' alignItems='center' gap={1}>
        {isCentralAdmin && (
          <FormControl size='small' sx={{ minWidth: 180 }}>
            <Select
              value={selectedClientId}
              displayEmpty
              onChange={(event) => setSelectedClientId(event.target.value)}
              sx={{ fontSize: 13 }}
              disabled={!clients.length}
              renderValue={(value) => {
                const selected = clients.find((client) => client.id === value);
                return selected?.name || 'All clients';
              }}
            >
              <MenuItem value=''>
                <em>All clients</em>
              </MenuItem>
              {clients.map((client) => (
                <MenuItem value={client.id} key={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button component={RouterLink} to={ROUTES.FAQ} color='inherit' size='small'>
          FAQ
        </Button>
        <UserMenu />
        <ThemeToggle />
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1,
            mx: 1,
          }}
        >
          <Tooltip title='Open Yodha' placement='bottom'>
            <Box
              component='a'
              href='https://oh.yodha.io/'
              target='_blank'
              rel='noreferrer'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                textDecoration: 'none',
                '& img': {
                  width: 30,
                  height: 30,
                  borderRadius: '4px',
                  display: 'block',
                },
              }}
              aria-label='Open Yodha'
            >
              <Box component='img' src={yodhaLogo} alt='Yodha' />
            </Box>
          </Tooltip>
          <Box
            component={RouterLink}
            to={ROUTES.LOGIN}
            sx={{
              maxWidth: '112px',
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
              textDecoration: 'none',
              color: 'inherit',
              '& svg, & img': {
                maxWidth: '112px',
                maxHeight: '37px',
              },
            }}
          >
            <BrandLogo />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
